/**
 * The button that turns "it broke" into a ticket, from where it broke.
 *
 * The form in Settings still exists, and it is the wrong place to be when an
 * analysis has just died: the person has to leave the failure, find Settings,
 * pick a category, and describe from memory a thing they can no longer see.
 * Most of them close the tab instead, and the failure is never reported at
 * all.
 *
 * So the design constraint here is **typing is optional**. Submit is enabled
 * from the moment the dialog opens, because the report is already worth
 * sending without a word from the customer: the job id carries the structured
 * cause from layer 1, and the browser carries its diagnostics from layer 2.
 * The textarea is there for the one thing the system genuinely cannot know —
 * what they were trying to do — and never blocks the report.
 */
import { useState } from 'react';
import { COMPANY } from '@/legal/company';
import { AlertCircle, Check, LifeBuoy, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useLanguage } from '@/contexts/LanguageContext';
import { reportProblem, type ProblemType } from '@/lib/reportProblem';
import { cn } from '@/lib/utils';

interface ReportProblemButtonProps {
  /** The analysis this is about. Unlocks the whole server-side job context. */
  jobId?: string | null;
  projectId?: string | null;
  /** The error already on screen. Sent as the machine summary. */
  errorMessage?: string | null;
  type?: ProblemType;
  variant?: 'outline' | 'ghost' | 'secondary';
  size?: 'sm' | 'default';
  className?: string;
}

export function ReportProblemButton({
  jobId,
  projectId,
  errorMessage,
  type = 'analysis',
  variant = 'outline',
  size = 'sm',
  className,
}: ReportProblemButtonProps) {
  const { language } = useLanguage();
  const isEl = language === 'el';

  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [sending, setSending] = useState(false);
  // Held in this component rather than a toast: once a report is filed, the
  // control has to *stay* changed. A toast disappears, and a person who is not
  // sure it worked clicks again — which is how one problem becomes five
  // tickets, the exact thing the dedup on the server side is there to prevent.
  const [ticketNumber, setTicketNumber] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  const submit = async () => {
    setSending(true);
    setFailed(null);
    const result = await reportProblem({
      type,
      description: description.trim() || undefined,
      jobId: jobId ?? undefined,
      projectId: projectId ?? undefined,
      errorMessage: errorMessage ?? undefined,
    });
    setSending(false);

    if (result.ok) {
      setTicketNumber(result.ticketNumber ?? '—');
      return;
    }

    // Saying "failed" and nothing else, inside a dialog whose whole purpose is
    // reporting a failure, is a dead end. The address is the way out.
    setFailed(
      isEl
        ? `Δεν στάλθηκε (${result.error ?? 'άγνωστο σφάλμα'}). Στείλε mail στο ${COMPANY.emails.support}.`
        : `It did not send (${result.error ?? 'unknown error'}). Please email ${COMPANY.emails.support}.`,
    );
  };

  if (ticketNumber) {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary',
          className,
        )}
      >
        <Check className="h-3.5 w-3.5" />
        {isEl ? `Στάλθηκε · ${ticketNumber}` : `Reported · ${ticketNumber}`}
      </span>
    );
  }

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={size}
        className={cn('bg-background', className)}
        onClick={() => setOpen(true)}
      >
        <LifeBuoy className="mr-2 h-4 w-4" />
        {isEl ? 'Ανέφερε το πρόβλημα' : 'Report a problem'}
      </Button>

      <Dialog open={open} onOpenChange={(next) => !sending && setOpen(next)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{isEl ? 'Ανέφερε το πρόβλημα' : 'Report this problem'}</DialogTitle>
            <DialogDescription>
              {isEl
                ? 'Στέλνουμε μαζί τι έσπασε, πότε, και σε ποια σελίδα. Δεν χρειάζεται να γράψεις κάτι.'
                : 'What broke, when, and on which page are sent with it. You do not need to write anything.'}
            </DialogDescription>
          </DialogHeader>

          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            maxLength={2000}
            placeholder={
              isEl
                ? 'Προαιρετικά: τι προσπαθούσες να κάνεις;'
                : 'Optional: what were you trying to do?'
            }
          />

          {failed && (
            <p className="flex items-start gap-2 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              {failed}
            </p>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={sending}>
              {isEl ? 'Άκυρο' : 'Cancel'}
            </Button>
            <Button onClick={submit} disabled={sending}>
              {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEl ? 'Στείλ’ το' : 'Send it'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
